import { useQuery } from "@apollo/client/react";
import { useMemo, useRef } from "react";
import { useSearchParams } from "react-router";

import {
  getFiltersPredicationFromURI,
  makeFilterItemFromSuggestionField,
  makeFilterItemOptionsFromSuggestionField,
} from "components/Filters/helpers";
import { useCachedFilterFields } from "components/Filters/hooks";
import { FilterItem, FiltersItemsOptionsMap, SortOption } from "components/Filters/types";
import { getSearchQuery } from "components/SearchInput/helpers";
import { PublicWorkerPool } from "types/generated";
import {
  FILTER_ITEMS_DICTIONARY,
  FilterItemKeys,
} from "views/Account/WorkerPool/QueuedRunsView/constants";

import { SEARCH_PUBLIC_WORKER_POOL_SCHEDULABLE_RUNS_SUGGESTIONS } from "./gql";

const useSearchPublicQueuedRunsSuggestions = () => {
  const fieldsRef = useRef<string[] | null>(null);
  const [searchParams] = useSearchParams();
  const searchInput = getSearchQuery(searchParams);

  const predicates = useMemo(() => {
    const predicatesMap = getFiltersPredicationFromURI(searchParams, true);

    return [...(predicatesMap?.values() || [])];
  }, [searchParams]);

  const { data, loading, refetch, previousData } = useQuery<{
    publicWorkerPool: PublicWorkerPool;
  }>(SEARCH_PUBLIC_WORKER_POOL_SCHEDULABLE_RUNS_SUGGESTIONS, {
    variables: {
      input: {
        predicates,
        fullTextSearch: searchInput,
        fields: fieldsRef.current,
      },
    },
  });

  const rawSearchRunsSuggestions = useMemo(() => {
    if (data?.publicWorkerPool) {
      const { searchSchedulableRunsSuggestions } = data.publicWorkerPool;

      return searchSchedulableRunsSuggestions;
    }

    return undefined;
  }, [data]);

  const cachedFiltersData = useCachedFilterFields(rawSearchRunsSuggestions?.fields);

  const sortOptions = useMemo((): SortOption[] => {
    return cachedFiltersData
      .filter((field) => field.orderable)
      .map((field) => {
        return {
          value: field.name,
          label: field.name,
        };
      });
  }, [cachedFiltersData]);

  const [filters, filtersMap] = useMemo<[FilterItem[], FiltersItemsOptionsMap]>(() => {
    const filtersItemsOptionsMap: FiltersItemsOptionsMap = new Map([]);

    const filterItems = cachedFiltersData
      .filter((field) => {
        // the priority changing functionality is not supported for Public Worker Pools
        if (field.filterable && field.name === FilterItemKeys.PRIORITY_PRESET) {
          return false;
        }

        return field.filterable;
      })
      .map((field) => {
        const options = makeFilterItemOptionsFromSuggestionField(field);

        if (options) {
          filtersItemsOptionsMap.set(field.name, options);
        }

        return makeFilterItemFromSuggestionField({ field, dictionary: FILTER_ITEMS_DICTIONARY });
      });

    return [filterItems, filtersItemsOptionsMap];
  }, [cachedFiltersData]);

  const handleRefetchActiveSections = (fields: string[]) => {
    fieldsRef.current = fields.length > 0 ? fields : null;

    if (fields.length > 0) {
      refetch({
        input: {
          fullTextSearch: searchInput,
          predicates,
          fields,
        },
      });
    }
  };

  const previousSearchRunsSuggestions =
    previousData?.publicWorkerPool?.searchSchedulableRunsSuggestions;

  return {
    filters,
    filtersMap,
    sortOptions,
    loading,
    refetchActiveSections: handleRefetchActiveSections,
    filteredCount:
      rawSearchRunsSuggestions?.filteredCount || previousSearchRunsSuggestions?.filteredCount || 0,
  };
};

export default useSearchPublicQueuedRunsSuggestions;
